import { Field } from 'contensis-core-api';
import { Validations } from 'contensis-core-api/lib/models/Validations';
import { EntryAsset } from 'contensis-delivery-api';

export type FieldAssertion = (field: Field) => boolean;

export class ContentTypeFieldValidationAssertions {
  /** For a canvas field with the "kitchen sink" configuration */
  static IsAllowedTypesAll: FieldAssertion = (field) =>
    !!(field?.validations as any)?.allowedTypes?.types?.find(
      (type: any) => type.type === '*'
    );

  static IsRequired: FieldAssertion = (field) =>
    !!field?.validations?.required || !!field?.validations?.minCount?.value;

  static HasMinLength: FieldAssertion = (field) =>
    !!field?.validations?.minLength?.value;

  static HasMaxLength: FieldAssertion = (field) =>
    !!field?.validations?.maxLength?.value;

  static HasRegex: FieldAssertion = (field) =>
    !!field?.validations?.regex?.pattern;

  static IsAltTextRequired: FieldAssertion = (field) =>
    !!field?.validations?.altTextRequired;

  static HasImageDimensions: FieldAssertion = (field) =>
    !!field?.validations?.imageDimensions;

  static HasAllowedContentTypes = (
    field: Field
  ): field is Field & {
    validations: { allowedContentTypes: { contentTypes: string[] } };
  } => !!field?.validations?.allowedContentTypes?.contentTypes?.length;

  static ImageDimensions = (field: Field, image: EntryAsset) => {
    const { imageDimensions } =
      // check canvas field validations for image type validations
      ((field?.validations as any)?.allowedTypes?.types.find(
        (t: any) => t.type === '_image'
      ) as Validations<Field>) ||
      // check for regular image field validations
      field?.validations ||
      {};
    if (!imageDimensions) return true;
    else {
      const { minWidth, minHeight, maxWidth, maxHeight } = imageDimensions;
      const { width, height } = image?.sys?.properties || {};
      const validations = `${minWidth ? ` minWidth: ${minWidth}` : ''}${maxWidth ? ` maxWidth: ${maxWidth}` : ''}${minHeight ? ` minHeight: ${minHeight}` : ''}${maxHeight ? ` maxHeight: ${maxHeight}` : ''} for width: ${width}, height: ${height}`;
      if (!width || !height)
        return `could not validate image dimensions${validations} id: ${image.sys?.id}`;

      const failed: string[] = [];
      if (minWidth && width < minWidth)
        failed.push(`width: ${width} for minWidth: ${minWidth}`);
      if (maxWidth && width > maxWidth)
        failed.push(`width: ${width} for maxWidth: ${maxWidth}`);
      if (minHeight && height < minHeight)
        failed.push(`height: ${height} for minHeight: ${minHeight}`);
      if (maxHeight && height > maxHeight)
        failed.push(`height: ${height} for maxHeight: ${maxHeight}`);
      if (failed.length)
        return `invalid image dimensions ${failed.join(', ')} id: ${image.sys?.id}`;
      return true;
    }
  };
}

// Text:

// !!field?.validations?.minLength?.value
// !!field?.validations?.regex?.pattern

// Images:

// "field?.validations?.altTextRequired": {}
// "field?.validations?.imageDimensions": {
//   "minWidth": 1000,
//   "maxWidth": 1000,
//   "minHeight": 1000,
//   "maxHeight": 1000
// },

// Entries:

// "field?.validations?.allowedContentTypes?.contentTypes": []

// Composer:

// none found, not even required

// Canvas:

// Links, Headings, Images [dimensions, alt text, caption], Paragraph, Lists, Panels, Code[all], Components[all], Content Types[all], Asset types[all], Forms[all], Tables, Quotes, Dividers, Embeds[all], Buttons[all]

// "field?.validations?.allowedTypes?.types": []
// "types": [
//   {
//     "type": "*"
//   },
//   {
//     "type": "_fragment",
//     "decorators": {
//       "allowed": [
//         {
//           "decorator": "*"
//         }
//       ]
//     }
//   },
//   {
//     "type": "_inlineEntry",
//     "linkContentTypes": {
//       "allowed": [
//         "simpleContent"
//       ]
//     }
//   },
//   {
//     "type": "_link",
//     "linkContentTypes": {
//       "allowed": [
//         "simpleContent"
//       ]
//     }
//   },
//   {
//     "type": "_component",
//     "components": {
//       "allowed": [
//         "linkedComponent",
//         "nestingLinkedComponent"
//       ]
//     }
//   },
//   {
//     "type": "_image",
//     "imageDimensions": {
//       "minWidth": 1000,
//       "maxWidth": 1000,
//       "minHeight": 1000,
//       "maxHeight": 1000
//     },
//     "captionRequired": {
//       "message": {
//         "en-GB": ""
//       }
//     },
//     "altTextRequired": {
//       "message": {
//         "en-GB": ""
//       }
//     }
//   }
// ]
